/* eslint-disable @typescript-eslint/no-explicit-any */
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  computed,
  contentChildren,
  inject,
  input,
  output,
  viewChild,
  viewChildren,
} from '@angular/core';

import { MovableCardDirective } from './directives/movable-card.directive';
import { MovableCardsContainerGroupTypeEnum, MovableCardsContainerViewModel, SimpleDomRect } from './movable-cards-container.view-model';
import { BoundingRectMapService } from './services/bounding-rect-map.service';
import { DragAreaService } from './services/drag-area.service';
import { RearrangeService } from './services/rearrange.service';
import { StateService } from './services/state.service';

@Component({
  selector: 'app-movable-cards-container',
  imports: [NgTemplateOutlet, NgClass],
  providers: [DragAreaService, BoundingRectMapService, StateService, RearrangeService],
  templateUrl: './movable-cards-container.component.html',
  styleUrl: './movable-cards-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MovableCardsContainerComponent<T extends string = string> implements OnChanges, OnDestroy {
  readonly #document = inject(DOCUMENT);
  readonly #state = inject<StateService<T>>(StateService<T>);
  readonly #dragAreaService = inject<DragAreaService<T>>(DragAreaService);
  readonly #boundingRectMapService = inject<BoundingRectMapService<T>>(BoundingRectMapService);
  readonly #rearrangeService = inject<RearrangeService<T>>(RearrangeService);

  public vm = input.required<MovableCardsContainerViewModel<T>>();
  public layoutChange = output<MovableCardsContainerViewModel<T>>();
  public directives = contentChildren(MovableCardDirective<T>);
  public cardsContainer = viewChild<ElementRef<HTMLElement>>('cardsContainer');
  public cardContainers = viewChildren<ElementRef<HTMLElement>>('cardContainer');
  public cardContainerRect = this.#state.cardContainerRect;
  public dragAreasDebugMode = false;
  public readonly MovableCardsContainerGroupTypeEnum = MovableCardsContainerGroupTypeEnum;
  public dragAreas = this.#state.dragAreas;
  public keyTransformMap = this.#state.keyTransformMap;
  public containerMinHeight = this.#state.containerMinHeight;
  public stableGroups = this.#state.stableGroups;
  public dragIsInProgress = this.#state.dragIsInProgress;
  readonly #allDraggingClassName = 'all-dragging';

  public draggedCardKey = computed(() => {
    const draggedKey = this.#state.draggedCardKey;
    const dragIsInProgress = this.#state.dragIsInProgress();

    return dragIsInProgress ? draggedKey : null;
  });

  public leadingCardContainer = computed(() => {
    const groups = this.#state.stableGroups();
    const firstGroup = groups[0];

    if (!firstGroup) {
      return null;
    }

    const leadingCardKey =
      firstGroup.type === MovableCardsContainerGroupTypeEnum.SingleColumn
        ? firstGroup.cards[0]?.key
        : (firstGroup.leftCards[0] ?? firstGroup.rightCards[0])?.key;

    if (!leadingCardKey) {
      return null;
    }

    const cardKeys = groups.flatMap((group) =>
      group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
        ? group.cards.map((card) => card.key)
        : [...group.leftCards, ...group.rightCards].map((card) => card.key)
    );
    const leadingCardIndex = cardKeys.indexOf(leadingCardKey);

    return leadingCardIndex === -1 ? null : (this.cardContainers()[leadingCardIndex] ?? null);
  });

  public keyDirectiveMap = computed(() => {
    const directives = this.directives();
    return directives.reduce(
      (acc, directive) => {
        acc[directive.appMovableCardKey()] = directive;
        this.setDirectiveHandlers(directive.appMovableCardKey(), directive);
        return acc;
      },
      {} as Record<string, MovableCardDirective<T>>
    );
  });

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['vm']) {
      this.#state.setStableGroups(this.vm().groups);
    }
  }

  public ngOnDestroy(): void {
    if (this.#state.dragIsInProgress()) {
      this.clearDraggingCursor();
    }
  }

  public setDirectiveHandlers(key: string, directive: MovableCardDirective<T>): void {
    directive.setOnDragStarted(() => {
      this.#state.setDragIsInProgress(true);
      this.setDraggingCursor();
      this.#state.setDraggedCardKey(key);
      this.#state.setKeyContainerMap(this.buildKeyContainerMap());
      this.measureElements();
      this.#state.setGroupsDuringDrag(this.#state.stableGroups());
      this.#state.setDragAreas(this.#dragAreaService.buildDragAreas(key, this.#state.stableGroups()));
    });
    directive.setOnDragMoved((event) => {
      this.handleDragMoved(event);
    });
    directive.setOnDragEnded((event) => {
      this.#state.setDragIsInProgress(false);
      this.clearDraggingCursor();
      event.source.reset();
      this.#state.setContainerMinHeight(null);
      this.#state.setKeyTransformMap({});
      this.#state.setStableGroups(this.#state.groupsDuringDrag);
      this.layoutChange.emit({ groups: this.#state.stableGroups() });
    });
    directive.setToggleWideMode(() => {
      this.handleWideModeToggle(key);
    });
  }

  private handleDragMoved(event: CdkDragMove<any>): void {
    const firstDragAreaThatContainsCursor = this.#state.dragAreas().find((area) => {
      const pointerX = event.pointerPosition.x;
      const pointerY = event.pointerPosition.y + window.scrollY;
      return pointerX >= area.left && pointerX <= area.left + area.width && pointerY >= area.top && pointerY <= area.top + area.height;
    });

    if (firstDragAreaThatContainsCursor) {
      const newGroups = firstDragAreaThatContainsCursor.swapFn();
      if (JSON.stringify(newGroups) !== JSON.stringify(this.#state.groupsDuringDrag)) {
        const draggedKey = this.#state.draggedCardKey;
        this.#state.setGroupsDuringDrag(newGroups);
        const { rectMap, totalHeight } = this.#boundingRectMapService.buildBoundingRectMapFromGroups(newGroups);
        const keyTransformMap = this.buildKeyTransformMap(this.#state.initialKeyBoundingRectMap, rectMap);
        this.#state.setContainerMinHeight(totalHeight);
        this.#state.setKeyTransformMap(keyTransformMap);
        this.#state.setDragAreas(this.#dragAreaService.buildDragAreas(draggedKey, this.#state.groupsDuringDrag));
      }
    }
  }

  private handleWideModeToggle(cardKey: string): void {
    this.#state.setKeyContainerMap(this.buildKeyContainerMap());
    this.measureElements();
    const newGroups = this.#rearrangeService.toggleWideModeOfCard(
      this.#state.stableGroups(),
      this.#state.initialKeyBoundingRectMap,
      cardKey
    );
    this.#state.setStableGroups(newGroups);
    this.layoutChange.emit({ groups: newGroups });
  }

  private measureElements(): void {
    const scrollY = window.scrollY;
    const containerRect = this.cardsContainer()!.nativeElement.getBoundingClientRect();
    this.#state.setCardContainerRect({
      width: containerRect.width,
      height: containerRect.height,
      top: containerRect.top + scrollY,
      left: containerRect.left,
    });
    const keys = this.#state.keyContainerMap;
    Object.keys(keys).forEach((key) => {
      const rect = keys[key].nativeElement.getBoundingClientRect();
      this.#state.patchInitialKeyBoundingRectMap(key, {
        width: rect.width,
        height: rect.height,
        left: rect.left,
        top: rect.top + scrollY,
      });
    });
  }

  private buildKeyContainerMap() {
    const containers = this.cardContainers();
    const groups = this.#state.stableGroups();
    const map: Record<string, ElementRef<HTMLElement>> = {};

    let idx = 0;
    groups.forEach((group) => {
      if (group.type === MovableCardsContainerGroupTypeEnum.SingleColumn) {
        group.cards.forEach((card) => {
          map[card.key] = containers[idx];
          idx++;
        });
      } else {
        group.leftCards.forEach((card) => {
          map[card.key] = containers[idx];
          idx++;
        });
        group.rightCards.forEach((card) => {
          map[card.key] = containers[idx];
          idx++;
        });
      }
    });

    return map;
  }

  private buildKeyTransformMap(
    originalKeyRectMap: Record<string, SimpleDomRect>,
    newKeyRectMap: Record<string, SimpleDomRect>
  ): Record<string, string> {
    const map: Record<string, string> = {};
    const draggedKey = this.#state.draggedCardKey;

    Object.keys(originalKeyRectMap).forEach((key) => {
      if (key === draggedKey) {
        map[key] = 'translate(0, 0)';
        return;
      }

      const originalRect = originalKeyRectMap[key];
      const newRect = newKeyRectMap[key];

      const topDiff = newRect.top - originalRect.top;
      const leftDiff = newRect.left - originalRect.left;

      map[key] = `translate(${leftDiff}px, ${topDiff}px)`;
    });

    return map;
  }

  private setDraggingCursor(): void {
    this.#document.body.classList.add(this.#allDraggingClassName);
  }

  private clearDraggingCursor(): void {
    this.#document.body.classList.remove(this.#allDraggingClassName);
  }
}
